<template>
  <div 
    class="" 
    :data-type="media?.type" 
    :data-id="id">
    <!-- IMAGE -->
    
    <img
      v-if="media?.type === 'image'"
      :src="media.url"
      :alt="altText"
      :loading="loadingAttr"
      :decoding="decodingAttr"
      class="h-full w-full object-cover"
    />

    <!-- VIDEO -->
    <video
      v-else-if="media?.type === 'video'"
      class="media media-video"
      :poster="media.poster"
      :autoplay="autoplay"
      :muted="muted"
      :loop="loop"
      :controls="controls"
      playsinline
      preload="metadata"
    >
      <source :src="media.url" />
    </video>

    <!-- FALLBACK -->
    <div v-else class="media-fallback" aria-hidden="true" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useMedia } from '@/composables/useMedia'
import { useContentStore } from '@/stores/content.store'

type Props = {
  /**
   * Attachment ID (WP media ID)
   */
  id?: number | null

  /**
   * Requested image size key (e.g. 'hero', 'large', 'full')
   */
  imageSize?: string

  /**
   * Override alt text (optional)
   */
  alt?: string

  /**
   * Image loading behavior
   */
  loading?: 'lazy' | 'eager'

  /**
   * Image decoding behavior
   */
  decoding?: 'async' | 'auto' | 'sync'

  /**
   * Video options
   */
  autoplay?: boolean
  muted?: boolean
  loop?: boolean
  controls?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  imageSize: 'full',
  loading: 'lazy',
  decoding: 'async',
  autoplay: false,
  muted: true,
  loop: true,
  controls: false,
})

/**
 * Media resolver (reads from content.store.attachments)
 */
const { media } = useMedia(props.id, { imageSize: props.imageSize })

/**
 * Optional safety: if attachments aren't loaded yet, load media on demand.
 * This won't refetch if cache is fresh (your store TTL handles that).
 */
const contentStore = useContentStore()
onMounted(async () => {
  if (!props.id) return
  if (contentStore.attachments.length > 0) return

  // This will respect TTL/cache and only fetch if needed
  await contentStore.loadAttachments()
})

const altText = computed(() => {
  if (props.alt) return props.alt
  if (media.value?.type === 'image') return media.value.alt ?? ''
  return ''
})

const loadingAttr = computed(() => props.loading)
const decodingAttr = computed(() => props.decoding)

const autoplay = computed(() => props.autoplay)
const muted = computed(() => props.muted)
const loop = computed(() => props.loop)
const controls = computed(() => props.controls)
</script>

<style scoped>
.media-wrapper {
  display: block;
  position: relative;
}

.media {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-fallback {
  width: 100%;
  height: 100%;
}
</style>
